import * as React from "react";
import Keycloak from 'keycloak-js';
import * as actions from "../../actions";
import { ErrorMessage, StoreState } from "../../types";
import { connect } from "react-redux";
import { Dispatch } from "redux";import Api from "../../api";
import { Facility, LocalizedValue, TreeSpecies } from "../../generated/client";
import { Navigate } from 'react-router-dom';
import strings from "../../localization/strings";

import {
  Grid,
  Button,
  Loader,
  Form,
  Message,
  Confirm,
  Input
} from "semantic-ui-react";
import LocalizedValueInput from "../LocalizedValueInput";
import { FormContainer } from "../FormContainer";

/**
 * Interface representing component properties
 */
interface Props {
  keycloak?: Keycloak;
  facility: Facility;
  treeSpeciesId: string;
  tree?: TreeSpecies;
  onTreeSpeciesSelected?: (treeSpecies: TreeSpecies) => void;
  onTreeSpeciesDeleted?: (treeSpeciesId: string) => void;
  onError: (error: ErrorMessage | undefined) => void
}

/**
 * Interface representing component state
 */
interface State {
  tree?: TreeSpecies;
  redirect: boolean;
  saving: boolean;
  messageVisible: boolean;
  open:boolean;
}

/**
 * React component for edit tree species view
 */
class EditTreeSpecies extends React.Component<Props, State> {

  /**
   * Constructor
   * @param props component properties
   */
  constructor(props: Props) {
    super(props);
    this.state = {
      tree: undefined,
      redirect: false,
      saving: false,
      messageVisible: false,
      open:false
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
  }

  /**
   * Component did mount life-sycle method
   */
  public async componentDidMount() {
    const { treeSpeciesId, keycloak, facility, onError, onTreeSpeciesSelected } = this.props;
    try {
      if (!keycloak) {
        return;
      }

      const treeSpeciesService = await Api.getTreeSpeciesService(keycloak);
      const treeSpecies = await treeSpeciesService.findTreeSpecies({
        treeSpeciesId: treeSpeciesId,
        facility: facility
      });
      onTreeSpeciesSelected && onTreeSpeciesSelected(treeSpecies);
      this.setState({tree: treeSpecies});

    } catch (e: any) {
      Api.handleApiError(e, onError);
    }
  }

  /**
   *  Updates tree species name
   *
   * @param name localized entry representing name
   */
  updateName = (name: LocalizedValue[]) => {
    this.setState({
      tree: {...this.state.tree, name: name}
    });
  }

  /**
   * Handle form submit
   */
  private async handleSubmit() {
    const { keycloak, facility, onError } = this.props;
    const { tree } = this.state;
    try {
      if (!keycloak || !tree) {
        return;
      }

      this.setState({saving: true});

      const treeSpeciesService = await Api.getTreeSpeciesService(keycloak);
      await treeSpeciesService.updateTreeSpecies({
        treeSpeciesId: this.props.treeSpeciesId,
        treeSpecies: tree,
        facility: facility
      });

      this.setState({saving: false});

      this.setState({messageVisible: true});
      setTimeout(() => {
        this.setState({messageVisible: false});
      }, 3000);
    } catch (e: any) {
      Api.handleApiError(e, onError);
    }
  }

  /**
   * Handle performedCultivationAction delete
   */
  private async handleDelete() {
    const { keycloak, facility, onError, onTreeSpeciesDeleted, treeSpeciesId } = this.props;
    try {
      if (!keycloak) {
        return;
      }

      const treeSpeciesService = await Api.getTreeSpeciesService(keycloak);
      await treeSpeciesService.deleteTreeSpecies({
        treeSpeciesId: treeSpeciesId,
        facility: facility
      });

      onTreeSpeciesDeleted && onTreeSpeciesDeleted(treeSpeciesId);
      this.setState({redirect: true});
    } catch (e: any) {
      Api.handleApiError(e, onError);
    }
  }

  /**
   * Render edit treeSpecies view
   */
  public render() {
    if (!this.props.treeSpeciesId) {
      return (
        <Grid style={{paddingTop: "100px"}} centered >
          <Loader inline active size="medium" />
        </Grid>
      );
    }

    if (this.state.redirect) {
      return <Navigate replace={true} to="/treeSpecies"/>;
    }
    const treeName = this.state.tree && this.state.tree.name ? this.state.tree!.name![0].value : "";

    return (
      <Grid>
        <Grid.Row className="content-page-header-row">
          <Grid.Column width={6}>
            <h2>{treeName}</h2>
          </Grid.Column>
          <Grid.Column width={3} floated="right">
            <Button className="danger-button" onClick={()=>this.setState({open:true})}>{strings.delete}</Button>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={8}>
            <FormContainer>
              <Form.Field required>
                <label>{strings.treeSpeciesName}</label>
                <LocalizedValueInput
                  onValueChange={this.updateName}
                  value={this.state.tree ? this.state.tree.name : undefined}
                  languages={["fi", "en"]}
                />
              </Form.Field>
              <Form.Field>
              <label>{strings.treeSpeciesCode}</label>
                <Input
                  value={this.state.tree?.code ?? ""}
                  onChange={(e) => 
                    this.setState({tree: {...this.state.tree!, code: e.currentTarget.value}})
                  }
                />
              </Form.Field>
              <Message
                success
                visible={this.state.messageVisible}
                header={strings.savedSuccessfully}
              />
              <Button
                className="submit-button"
                onClick={this.handleSubmit}
                type='submit'
                loading={this.state.saving}
              >
                  {strings.save}
              </Button>
            </FormContainer>
          </Grid.Column>
        </Grid.Row>
        <Confirm open={this.state.open} size={"mini"} content={strings.deleteConfirmationText + treeName} onCancel={()=>this.setState({open:false})} onConfirm={this.handleDelete} />
      </Grid>
    );
  }
}

/**
 * Redux mapper for mapping store state to component props
 *
 * @param state store state
 */
export function mapStateToProps(state: StoreState) {
  return {
    treeSpecies: state.treeSpecies,
    tree: state.tree,
    facility: state.facility
  };
}

/**
 * Redux mapper for mapping component dispatches
 *
 * @param dispatch dispatch method
 */
export function mapDispatchToProps(dispatch: Dispatch<actions.AppAction>) {
  return {
    onTreeSpeciesSelected: (tree: TreeSpecies) => dispatch(actions.treeSpeciesSelected(tree)),
    onTreeSpeciesDeleted: (treeSpeciesId: string) => dispatch(actions.treeSpeciesDeleted(treeSpeciesId)),
    onError: (error: ErrorMessage | undefined) => dispatch(actions.onErrorOccurred(error))
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(EditTreeSpecies);